import React, { useEffect, useState } from "react";
import client, { formatApiErrorDetail } from "@/lib/api";
import { useCart } from "@/context/CartContext";
import { useAuth } from "@/context/AuthContext";
import { Check, X, Sparkles } from "lucide-react";

export default function PromoCodeInput({ compact = false }) {
  const { promoCode, setPromoCode } = useCart();
  const { user } = useAuth();
  const [input, setInput] = useState(promoCode || "");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [success, setSuccess] = useState("");
  const [myCodes, setMyCodes] = useState([]);

  // Fetch unused arcade/reward codes when the user is logged in
  useEffect(() => {
    if (!user) { setMyCodes([]); return; }
    client.get("/me/promo-codes")
      .then((r) => setMyCodes(r.data?.available || []))
      .catch(() => setMyCodes([]));
  }, [user, promoCode]);

  const apply = async (e, codeOverride) => {
    e?.preventDefault?.();
    const code = (codeOverride || input || "").trim().toUpperCase();
    if (!code) return;
    setBusy(true); setErr(""); setSuccess("");
    try {
      const r = await client.post("/promo/validate", { code });
      setPromoCode(code);
      const promo = r.data;
      const valueLabel = promo.kind === "percent" ? `${promo.value}% off` : `£${Number(promo.value).toFixed(2)} off`;
      setSuccess(`${code} · ${valueLabel}`);
    } catch (e) {
      setErr(formatApiErrorDetail(e.response?.data?.detail) || "Invalid code");
    } finally {
      setBusy(false);
    }
  };

  const remove = () => {
    setPromoCode("");
    setInput("");
    setSuccess("");
    setErr("");
  };

  const padding = compact ? "py-2" : "py-3";

  const [showOptions, setShowOptions] = useState(false);

  // Auto-apply best available code on mount if the user hasn't set one yet.
  // Single-promo rule is enforced server-side: only one is honoured per order.
  useEffect(() => {
    if (!user || promoCode || myCodes.length === 0) return;
    const best = myCodes[0];
    if (!best?.code) return;
    apply(null, best.code).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, myCodes.length, promoCode]);

  if (promoCode) {
    const others = myCodes.filter((c) => c.code !== promoCode);
    return (
      <div className="space-y-2" data-testid="promo-applied-wrap">
        <div className={`flex items-center justify-between gap-3 border border-[#FF3B30] bg-[#FFF5F4] px-3 ${padding}`} data-testid="promo-applied">
          <div className="flex items-center gap-2 text-sm">
            <Check size={14} className="moto-accent" strokeWidth={2} />
            <span className="font-medium">{promoCode}</span>
            {success && <span className="text-black/50">— {success.split(" · ")[1] || ""}</span>}
          </div>
          <div className="flex items-center gap-3">
            {others.length > 0 && (
              <button onClick={() => setShowOptions((v) => !v)} className="label-eyebrow text-xs text-black/50 hover:moto-accent" data-testid="promo-switch-toggle">
                {showOptions ? "Hide" : "Switch"}
              </button>
            )}
            <button onClick={remove} className="label-eyebrow text-black/50 hover:moto-accent flex items-center gap-1" data-testid="promo-remove">
              <X size={12} /> Remove
            </button>
          </div>
        </div>
        {showOptions && others.length > 0 && (
          <div className="border border-black/10 bg-white p-2 space-y-1" data-testid="promo-switch-list">
            <div className="label-eyebrow text-[10px] text-black/40 px-2 pt-1">Switch to a different code (only one applies)</div>
            {others.map((c) => (
              <button
                key={c.code}
                type="button"
                onClick={(e) => { apply(e, c.code); setShowOptions(false); }}
                className="w-full flex items-center justify-between text-left px-2 py-1.5 text-sm hover:bg-[#FAFAFA]"
                data-testid={`promo-switch-${c.code}`}
              >
                <span className="font-medium">{c.code}</span>
                <span className="text-xs text-black/60">{c.kind === "percent" ? `${c.value}% off` : `£${Number(c.value).toFixed(2)} off`}</span>
              </button>
            ))}
          </div>
        )}
      </div>
    );
  }

  const bestCode = myCodes[0];

  return (
    <div className="space-y-3">
      {bestCode && (
        <button
          type="button"
          onClick={(e) => apply(e, bestCode.code)}
          className="w-full flex items-center justify-between gap-3 border border-[#FF3B30] bg-gradient-to-r from-[#FFF5F4] to-[#FFE6E2] px-3 py-2.5 hover:bg-[#FF3B30] hover:text-white transition-colors group"
          data-testid="promo-autoapply"
        >
          <span className="flex items-center gap-2 text-sm">
            <Sparkles size={14} className="moto-accent group-hover:text-white" />
            <span className="label-eyebrow moto-accent group-hover:text-white">Your reward</span>
            <span className="font-medium">{bestCode.code}</span>
          </span>
          <span className="text-sm font-medium moto-accent group-hover:text-white">
            Apply {bestCode.kind === "percent" ? `${bestCode.value}% off` : `£${Number(bestCode.value).toFixed(2)} off`}
          </span>
        </button>
      )}
      <form onSubmit={apply} className="flex gap-0 border-b border-black" data-testid="promo-form">
        <input
          value={input}
          onChange={(e) => { setInput(e.target.value.toUpperCase()); setErr(""); }}
          placeholder="Promo code"
          className={`flex-1 bg-transparent ${padding} text-sm focus:outline-none uppercase placeholder:normal-case placeholder:text-black/40`}
          data-testid="promo-input"
        />
        <button type="submit" disabled={busy || !input.trim()} className="label-eyebrow px-3 disabled:opacity-40" data-testid="promo-apply-btn">
          {busy ? "…" : "Apply"}
        </button>
      </form>
      {err && <div className="text-xs moto-accent mt-2" data-testid="promo-error">{err}</div>}
    </div>
  );
}
